import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";

import { INavLink } from "@/types";
import { sidebarLinks } from "@/constants";
import { Loader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { useSignOutAccount } from "@/lib/react-query/queries";
import { useUserContext, INITIAL_USER } from "@/context/AuthContext";
import NotificationDropdown from "./NotificationDropdown";
import { buildSignInRedirect } from "@/lib/auth/redirect";

const LeftSidebar = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user, setUser, setIsAuthenticated, isLoading, isAuthenticated } = useUserContext();
  const { mutate: signOut } = useSignOutAccount();

  const handleSignOut = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.preventDefault();
    signOut();
    setIsAuthenticated(false);
    setUser(INITIAL_USER);
    navigate(buildSignInRedirect("/home"));
  };

  const handleAuthRequired = (route: string) => {
    navigate(buildSignInRedirect(route));
  };

  return (
    <nav className="leftsidebar">
      <div className="flex flex-col gap-11">
        <Link to="/home" className="flex gap-3 items-center">
          <img
            src="/assets/images/busiconn.svg"
            alt="logo"
            width={250}
            height={50}
          />
        </Link>

        {/* User Profile Section - Only show if authenticated */}
        {isAuthenticated && (
          <>
            {isLoading || !user.emailOrPhone ? (
              <div className="h-14">
                <Loader />
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <Link to={`/profile/${user.id}`} className="flex gap-3 items-center flex-1">
                  <img
                    src={user.imageUrl || "/assets/icons/profile-placeholder.svg"}
                    alt="profile"
                    className="h-12 w-12 rounded-full"
                  />
                  <div className="flex flex-col w-full">
                    <p className="body-bold">{user.name}</p>
                    <p className="small-regular text-light-3">@{user.username}</p>
                  </div>
                </Link>
                <NotificationDropdown />
              </div>
            )}
          </>
        )}

        {/* Sign In/Up Section - Only show if not authenticated */}
        {!isAuthenticated && !isLoading && (
          <div className="flex flex-col gap-3 text-gray-700">
            <Button asChild className="shad-button_primary">
              <Link to={buildSignInRedirect("/home")}>
                <img src="/assets/icons/login.svg" alt="login" width={20} height={20} />
                Sign In
              </Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/sign-up">
                <img src="/assets/icons/people.svg" alt="signup" width={20} height={20} />
                Sign Up
              </Link>
            </Button>
            <p className="text-center text-xs text-light-3">
              Join to unlock all features
            </p>
          </div>
        )}

        {/* Navigation Links */}
        <ul className="flex flex-col gap-6">
          {sidebarLinks.map((link: INavLink) => {
            const isActive = pathname === link.route;
            const requiresAuth = link.requiresAuth === true;

            return (
              <li
                key={link.label}
                className={`leftsidebar-link group ${
                  isActive && "bg-primary-500"
                }`}>
                
                {/* If route requires auth and user is not authenticated */}
                {requiresAuth && !isAuthenticated ? (
                  <button
                    onClick={() => handleAuthRequired(link.route)}
                    className="flex gap-4 items-center p-4 w-full text-left"
                  >
                    <img
                      src={link.imgURL}
                      alt={link.label}
                      width={24}
                      height={24}
                      className="group-hover:invert-white opacity-60"
                    />
                    <span className="flex items-center gap-2">
                      {link.label}
                      <img src="/assets/icons/lock.svg" alt="locked" width={12} height={12} />
                    </span>
                  </button>
                ) : (
                  <NavLink
                    to={link.route}
                    className="flex gap-4 items-center p-4">
                    <img
                      src={link.imgURL}
                      alt={link.label}
                      width={24}
                      height={24}
                      className={`group-hover:invert-white ${
                        isActive && "invert-white"
                      }`}
                    />
                    {link.label}
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {/* Bottom Section */}
      <div className="flex flex-col gap-4">
        {/* Logout Button - Only show if authenticated */}
        {isAuthenticated && (
          <Button
            variant="ghost"
            size="sides"
            className="shad-button_ghost"
            onClick={(e) => handleSignOut(e)}>
            <img src="/assets/icons/logout.svg" alt="logout" />
            <p className="small-medium lg:base-medium">Logout</p>
          </Button>
        )}
        
        {/* Contact Info */}
        <div className="flex items-center gap-4 px-4 py-2">
          <img 
            src="/assets/icons/contact.svg" 
            alt="contact" 
            width={24}
            height={24}
            className="invert-white"
          />
          <div className="flex flex-col">
            <p className="small-medium lg:base-medium">Contact us</p>
            <p className="small-regular text-light-3">contact@busiconn.com</p>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default LeftSidebar;
